import { createContext, Dispatch, SetStateAction } from 'react';
import { edmsSignalCategory } from 'tf-pulse-shared';

// Channel types - matching EDMS ChannelType enum
export type ChannelType = 'Email' | 'Teams' | 'Grouped';

export interface Channel {
  id: string;
  name: string;
  type: ChannelType;
  icon: string;
}

export interface SignalTriggerSettings {
  triggerType: 'minimum' | 'maximum' | 'range';
  threshold: string;
  thresholdMax: string;
  triggerLogicType: 'percentage' | 'period' | 'spike';
  percentageThreshold?: string;
  evaluationWindow?: string;
  periodMinutes?: string;
  numberOfSpikes?: string;
  cooldownPeriod?: string;
  advancedSettingsExpanded?: boolean;
}

/**
 * One selected signal. EDMS reports some signal names under several
 * categories, so a signal is only identified by its name and category together.
 */
export interface SelectedSignal {
  signal: string;
  category: string;
  unit: string;
}

/**
 * The identity of a signal. A stored alarm can still carry the legacy
 * MachineAlarm category, so the category is normalised to the EDMS one.
 */
export function signalKey({
  signal,
  category,
}: Pick<SelectedSignal, 'signal' | 'category'>): string {
  return `${edmsSignalCategory(category)}:${signal}`;
}

export interface AlarmFormData {
  // Step 1: Alarm Configuration
  name: string;
  machines: string[];
  signals: SelectedSignal[];
  category: string;
  signalUnit: string;

  // Step 4: Trigger
  threshold: string;
  operation: 'greater' | 'less';

  // Step 4: Conditions
  triggerType: 'minimum' | 'maximum' | 'range';
  thresholdMax: string;

  // Step 3: Channels
  selectedChannels: string[];
  channelSearchQuery: string;
  channelTypeFilters: ChannelType[]; // Active channel type filters

  // Step 4: Trigger logic
  triggerLogicType: 'percentage' | 'period' | 'spike';
  percentageThreshold?: string;
  evaluationWindow?: string;
  periodMinutes?: string;
  numberOfSpikes?: string;
  cooldownPeriod?: string;

  // Multi-signal only: per-signal trigger/logic settings keyed by signalKey
  multiSignalSettings?: Record<string, SignalTriggerSettings>;
}

export interface AddAlarmDialogContextValue {
  // Form data
  formData: AlarmFormData;
  setFormData: Dispatch<SetStateAction<AlarmFormData>>;
  updateFormData: (updates: Partial<AlarmFormData>) => void;

  // Step management
  currentStep: number;
  setCurrentStep: (step: number) => void;

  // UI state
  advancedSettingsExpanded: boolean;
  setAdvancedSettingsExpanded: (expanded: boolean) => void;

  // Machine data
  selectedMachineIds: string[];
  availableMachines: Array<{ id: string; displayName: string }>;

  // Edit mode
  isEditMode: boolean;
  editingAlarmId: string | null;
}

export const AddAlarmDialogContext = createContext<
  AddAlarmDialogContextValue | undefined
>(undefined);
